import React, { useLayoutEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import styled from "styled-components";
import { Color, FontSize, transparentize } from "@glossy";
import { usePrevious } from "@lkf/hooks";

export interface IProps {
  $color: Color;
  count: number;
}

const Root = styled(motion.div)<Partial<IProps>>`
  display: inline-block;
  font-size: ${FontSize.ExtraSmall};
  text-align: center;
  color: ${Color.White};
  background-color: ${({ $color }) => $color};
  line-height: 13px;
  min-width: 13px;
  border-radius: 100%;
  opacity: ${({ count = 0 }) => count === 0 && 0};
`;

const getVariants = (shadowColor: string) => {
  const fat = `${shadowColor} 0 0 0 6px`;
  const slim = `${shadowColor} 0 0 0 -.01px`;
  return {
    undefinedToZero: { opacity: 0 },
    undefinedToPositive: { opacity: 1, transition: { duration: 0 } },
    increase: { opacity: 1, boxShadow: [fat, slim, slim] },
    decreaseToPositive: { opacity: 1, boxShadow: [slim, fat, slim] },
    decreaseToZero: { opacity: 0, boxShadow: [slim, fat, slim] },
  };
};

export default ({ $color, count = 0 }) => {
  const controls = useAnimation();
  const prevCount = usePrevious({ count })?.count;
  const shadowColor = transparentize(0.75, $color);

  useLayoutEffect(() => {
    controls.start(
      prevCount === undefined
        ? count === 0
          ? "undefinedToZero"
          : "undefinedToPositive"
        : count === 0
        ? "decreaseToZero"
        : prevCount > count
        ? "decreaseToPositive"
        : "increase"
    );
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [count]);

  return (
    <Root
      $color={$color}
      animate={controls}
      variants={getVariants(shadowColor)}
      transition={{ easings: "easeIn", duration: 0.3, times: [0, 0.999, 1] }}
    >
      {count}
    </Root>
  );
};
